import React, { useMemo, useState } from "react"

import CoachingRacingLineMap from "./CoachingRacingLineMap"
import RawTelemetryAnalytics from "./RawTelemetryAnalytics"
const { normalizeIRacingSampleAnalytics } = require("../../utils/sampleRawAnalytics")

const COLORS = {
    reference: "#5159a2",
    comparison: "#d97706",
    brake: "#dc2626",
    throttle: "#059669",
    grid: "#e5e7eb",
}

const formatDelta = (value) => `${value >= 0 ? "+" : ""}${value.toFixed(3)} s`
const formatDistance = (value) => `${Math.round(value)} m`
const formatTimestamp = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    return `${minutes}:${String(seconds % 60).padStart(2, "0")}`
}

function chartPath(points, key, width, height, min, max) {
    if (!points.length || max === min) return ""
    return points
        .map((point, index) => {
            const x = (point.distanceM / 4578.77) * width
            const y = height - ((point[key] - min) / (max - min)) * height
            return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`
        })
        .join(" ")
}

function TelemetryChart({ title, subtitle, series, min, max, unit }) {
    const width = 960
    const height = 220
    const values = series
        .flatMap((item) => item.points.map((point) => point[item.key]))
        .filter(Number.isFinite)
    const lower = Number.isFinite(min) ? min : Math.min(...values)
    const upper = Number.isFinite(max) ? max : Math.max(...values)
    const padding =
        Number.isFinite(min) && Number.isFinite(max)
            ? 0
            : Math.max((upper - lower) * 0.08, 0.02)
    const chartMin = lower - padding
    const chartMax = upper + padding

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                    <p className="mt-1 max-w-3xl text-sm text-gray-500">
                        {subtitle}
                    </p>
                </div>
                <div className="flex flex-wrap gap-3 text-xs font-semibold text-gray-600">
                    {series.map((item) => (
                        <span
                            className="inline-flex items-center gap-2"
                            key={item.label}
                        >
                            <span
                                className="h-0.5 w-5"
                                style={{ background: item.color }}
                            />
                            {item.label}
                        </span>
                    ))}
                </div>
            </div>
            <div className="overflow-hidden rounded-xl bg-gray-50 p-3">
                <svg
                    aria-label={title}
                    className="block h-auto w-full"
                    role="img"
                    viewBox={`0 0 ${width} ${height + 34}`}
                >
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                        const y = ratio * height
                        const value = chartMax - ratio * (chartMax - chartMin)
                        return (
                            <g key={ratio}>
                                <line
                                    stroke={COLORS.grid}
                                    strokeWidth="1"
                                    x1="0"
                                    x2={width}
                                    y1={y}
                                    y2={y}
                                />
                                <text
                                    fill="#6b7280"
                                    fontSize="18"
                                    x="6"
                                    y={Math.max(16, y - 5)}
                                >
                                    {value.toFixed(unit === "s" ? 2 : 0)} {unit}
                                </text>
                            </g>
                        )
                    })}
                    {series.map((item) => (
                        <path
                            d={chartPath(
                                item.points,
                                item.key,
                                width,
                                height,
                                chartMin,
                                chartMax
                            )}
                            fill="none"
                            key={item.label}
                            opacity={item.opacity || 1}
                            stroke={item.color}
                            strokeDasharray={item.dashed ? "10 8" : undefined}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={item.width || 3}
                        />
                    ))}
                    <text fill="#6b7280" fontSize="18" x="0" y={height + 29}>
                        Start
                    </text>
                    <text
                        fill="#6b7280"
                        fontSize="18"
                        textAnchor="end"
                        x={width}
                        y={height + 29}
                    >
                        4.58 km
                    </text>
                </svg>
            </div>
        </section>
    )
}

function CoachingMetric({ label, reference, comparison, unit = "" }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                {label}
            </p>
            <div className="mt-2 flex items-end justify-between gap-3">
                <div>
                    <span className="block text-xs text-gray-500">
                        Reference
                    </span>
                    <strong className="text-base text-[#5159a2]">
                        {reference ?? "Not recorded"}
                        {reference != null ? unit : ""}
                    </strong>
                </div>
                <div className="text-right">
                    <span className="block text-xs text-gray-500">
                        Your lap
                    </span>
                    <strong className="text-base text-amber-700">
                        {comparison ?? "Not recorded"}
                        {comparison != null ? unit : ""}
                    </strong>
                </div>
            </div>
        </div>
    )
}

const VideoReference = ({ section }) => {
    const selectedVideoUrl = `https://www.youtube.com/embed/${section.guide.videoId}?start=${section.guide.start}&end=${section.guide.end}&rel=0`

    return (
        <div>
            <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="font-bold text-gray-900">
                    See the markers and line
                </h3>
                <span className="whitespace-nowrap text-xs font-semibold text-gray-500">
                    {formatTimestamp(section.guide.start)} to{" "}
                    {formatTimestamp(section.guide.end)}
                </span>
            </div>
            <div
                className="aspect-video overflow-hidden rounded-xl bg-black"
                style={{ aspectRatio: "16 / 9" }}
            >
                <iframe
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="h-full w-full"
                    loading="eager"
                    referrerPolicy="origin"
                    src={selectedVideoUrl}
                    title={`${section.name} coaching reference`}
                />
            </div>
        </div>
    )
}

const IRacingTelemetryReport = ({ report }) => {
    const rawAnalytics = useMemo(() => normalizeIRacingSampleAnalytics(report), [report])
    const [comparisonLap, setComparisonLap] = useState(1)
    const comparisonKey =
        comparisonLap === 1 ? "lap1Comparison" : "lap6Comparison"
    const priorities = useMemo(
        () =>
            [...report.coachingSections].sort(
                (a, b) =>
                    b[comparisonKey].timeDeltaS - a[comparisonKey].timeDeltaS
            ),
        [comparisonKey, report.coachingSections]
    )
    const [selectedSectionId, setSelectedSectionId] = useState(
        report.coachingSections[0].id
    )
    const selectedSection =
        report.coachingSections.find(
            (section) => section.id === selectedSectionId
        ) || priorities[0]
    const selectedComparison = selectedSection[comparisonKey]
    const referenceTrace = report.traces.find(
        (trace) => trace.lapNumber === report.analysisLaps.referenceLap
    )
    const comparisonTrace = report.traces.find(
        (trace) => trace.lapNumber === comparisonLap
    )
    const referenceLapRecord = report.laps.find(
        (lap) => lap.number === report.analysisLaps.referenceLap
    )

    return (
        <article className="row-full bg-gray-50 pb-20 text-gray-900">
            <header className="border-b border-gray-200 bg-white">
                <div className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
                    <p className="text-sm font-bold uppercase tracking-widest text-[#5159a2]">
                        iRacing telemetry analysis
                    </p>
                    <div className="mt-3 flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <h1 className="max-w-5xl text-4xl font-bold leading-tight sm:text-5xl">
                                Porsche 911 GT3 R telemetry analysis at Algarve
                            </h1>
                            <p className="mt-4 max-w-3xl text-lg leading-8 text-gray-600">
                                Compare measured laps section by section, then
                                connect the time difference to the racing line,
                                braking, throttle and steering inputs.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4">
                            <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                Session reference
                            </span>
                            <strong className="mt-1 block text-3xl">
                                {referenceLapRecord.formattedTime}
                            </strong>
                            <small className="text-gray-500">Lap 4</small>
                        </div>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-7xl space-y-10 px-4 py-10">
                <section aria-labelledby="learning-title">
                    <div className="flex flex-wrap items-end justify-between gap-5">
                        <div>
                            <p className="text-sm font-bold uppercase tracking-wide text-[#5159a2]">
                                Your next improvements
                            </p>
                            <h2
                                className="mt-1 text-3xl font-bold"
                                id="learning-title"
                            >
                                Learn the lap section by section
                            </h2>
                            <p className="mt-2 max-w-2xl text-gray-600">
                                Choose a section to focus the racing line,
                                coaching and reference video on the same part of
                                the lap.
                            </p>
                        </div>
                        <div
                            aria-label="Comparison lap"
                            className="inline-flex rounded-xl border border-gray-200 bg-white p-1"
                            role="group"
                        >
                            {[1, 6].map((lap) => (
                                <button
                                    className={`rounded-lg px-4 py-2 text-sm font-bold transition ${
                                        comparisonLap === lap
                                            ? "bg-[#5159a2] text-white shadow"
                                            : "text-gray-700 hover:bg-gray-50"
                                    }`}
                                    key={lap}
                                    onClick={() => setComparisonLap(lap)}
                                    type="button"
                                >
                                    Lap {lap}
                                </button>
                            ))}
                        </div>
                    </div>

                    <nav
                        aria-label="Coaching sections"
                        className="mt-6 grid gap-3 md:grid-cols-3"
                    >
                        {priorities.map((section, index) => (
                            <button
                                aria-pressed={selectedSection.id === section.id}
                                className={`rounded-xl border p-4 text-left transition ${
                                    selectedSection.id === section.id
                                        ? "border-[#5159a2] bg-[#5159a2] text-white shadow-sm"
                                        : "border-gray-200 bg-white hover:border-[#5159a2]"
                                }`}
                                key={section.id}
                                onClick={() => setSelectedSectionId(section.id)}
                                type="button"
                            >
                                <span className="flex items-start justify-between gap-3">
                                    <strong>
                                        {index + 1}. {section.name}
                                    </strong>
                                    <b
                                        className={`flex-shrink-0 whitespace-nowrap ${
                                            selectedSection.id === section.id
                                                ? "text-white"
                                                : section[comparisonKey]
                                                      .timeDeltaS > 0
                                                ? "text-amber-700"
                                                : "text-emerald-700"
                                        }`}
                                    >
                                        {formatDelta(
                                            section[comparisonKey].timeDeltaS
                                        )}
                                    </b>
                                </span>
                                <small
                                    className={`mt-2 block ${
                                        selectedSection.id === section.id
                                            ? "text-blue-100"
                                            : "text-gray-500"
                                    }`}
                                >
                                    {formatDistance(section.startM)} to{" "}
                                    {formatDistance(section.endM)}
                                </small>
                            </button>
                        ))}
                    </nav>

                    <div className="mt-4 grid overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm lg:grid-cols-[1.15fr_1fr]">
                        <div className="min-h-[480px] bg-slate-950 p-3">
                            <CoachingRacingLineMap
                                onSectionSelect={setSelectedSectionId}
                                points={referenceTrace.points}
                                sections={priorities}
                                selectedSection={selectedSection}
                            />
                        </div>
                        <aside className="space-y-5 border-t border-gray-200 p-5 lg:border-l lg:border-t-0 sm:p-7">
                            <div>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-wide text-[#5159a2]">
                                            Coach&apos;s focus
                                        </p>
                                        <h3 className="mt-1 text-2xl font-bold">
                                            {selectedSection.name}
                                        </h3>
                                    </div>
                                    <span
                                        className={`flex-shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-sm font-bold ${
                                            selectedComparison.timeDeltaS > 0
                                                ? "bg-amber-100 text-amber-900"
                                                : "bg-emerald-100 text-emerald-900"
                                        }`}
                                    >
                                        {formatDelta(
                                            selectedComparison.timeDeltaS
                                        )}
                                    </span>
                                </div>
                                <p className="mt-3 leading-7 text-gray-700">
                                    {selectedSection.instruction}
                                </p>
                            </div>

                            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
                                <CoachingMetric
                                    comparison={
                                        selectedComparison.comparison
                                            .minSpeedKmh
                                    }
                                    label="Minimum speed"
                                    reference={
                                        selectedComparison.baseline.minSpeedKmh
                                    }
                                    unit=" km/h"
                                />
                                <CoachingMetric
                                    comparison={
                                        selectedComparison.comparison
                                            .brakeStartM
                                    }
                                    label="Brake begins"
                                    reference={
                                        selectedComparison.baseline.brakeStartM
                                    }
                                    unit=" m"
                                />
                                <CoachingMetric
                                    comparison={
                                        selectedComparison.comparison
                                            .throttleReturnM
                                    }
                                    label="Full throttle"
                                    reference={
                                        selectedComparison.baseline
                                            .throttleReturnM
                                    }
                                    unit=" m"
                                />
                            </div>

                            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                                <h3 className="font-bold text-[#424d8a]">
                                    Try this
                                </h3>
                                <p className="mt-1 text-sm leading-6 text-gray-700">
                                    {selectedSection.drill}
                                </p>
                            </div>
                        </aside>
                        <div className="border-t border-gray-200 p-5 sm:p-7 lg:col-span-2">
                            <VideoReference section={selectedSection} />
                        </div>
                    </div>
                </section>

                <section aria-labelledby="charts-title" className="space-y-8">
                    <div>
                        <p className="text-sm font-bold uppercase tracking-wide text-[#5159a2]">
                            Understand the difference
                        </p>
                        <h2
                            className="mt-1 text-3xl font-bold"
                            id="charts-title"
                        >
                            See what changed around the lap
                        </h2>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                        {report.sectors.map((sector) => {
                            const comparison = sector[comparisonKey]
                            return (
                                <div
                                    className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                                    key={sector.number}
                                >
                                    <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                        Sector {sector.number}
                                    </span>
                                    <strong
                                        className={`mt-2 block text-2xl ${
                                            comparison.timeDeltaS > 0
                                                ? "text-amber-700"
                                                : "text-emerald-700"
                                        }`}
                                    >
                                        {formatDelta(comparison.timeDeltaS)}
                                    </strong>
                                </div>
                            )
                        })}
                    </div>

                    <TelemetryChart
                        max={Math.max(
                            0.65,
                            ...comparisonTrace.points.map(
                                (point) => point.deltaS
                            )
                        )}
                        min={Math.min(
                            -0.15,
                            ...comparisonTrace.points.map(
                                (point) => point.deltaS
                            )
                        )}
                        series={[
                            {
                                label: `Lap ${comparisonLap} to lap 4`,
                                color: COLORS.comparison,
                                points: comparisonTrace.points,
                                key: "deltaS",
                                width: 4,
                            },
                        ]}
                        subtitle={`Positive values mean lap ${comparisonLap} is behind lap 4 at that point.`}
                        title="Lap delta"
                        unit="s"
                    />

                    <TelemetryChart
                        series={[
                            {
                                label: "Lap 4",
                                color: COLORS.reference,
                                points: referenceTrace.points,
                                key: "speedKmh",
                                width: 4,
                            },
                            {
                                label: `Lap ${comparisonLap}`,
                                color: COLORS.comparison,
                                points: comparisonTrace.points,
                                key: "speedKmh",
                                dashed: true,
                                width: 3,
                            },
                        ]}
                        subtitle="Compare entry, minimum and exit speed at the same place on the track."
                        title="Speed over distance"
                        unit="km/h"
                    />

                    <TelemetryChart
                        max={100}
                        min={0}
                        series={[
                            {
                                label: "Brake",
                                color: COLORS.brake,
                                points: comparisonTrace.points,
                                key: "brakePct",
                                width: 3,
                            },
                            {
                                label: "Throttle",
                                color: COLORS.throttle,
                                points: comparisonTrace.points,
                                key: "throttlePct",
                                width: 3,
                            },
                        ]}
                        subtitle={`Pedal inputs from lap ${comparisonLap}, aligned with the racing line and delta.`}
                        title="Brake and throttle"
                        unit="%"
                    />

                    <div className="grid gap-8 lg:grid-cols-2">
                        <TelemetryChart
                            series={[
                                {
                                    label: "Lap 4",
                                    color: COLORS.reference,
                                    points: referenceTrace.points,
                                    key: "steeringDeg",
                                    width: 3,
                                },
                                {
                                    label: `Lap ${comparisonLap}`,
                                    color: COLORS.comparison,
                                    points: comparisonTrace.points,
                                    key: "steeringDeg",
                                    dashed: true,
                                    width: 3,
                                },
                            ]}
                            subtitle="Compare rotation timing and extra corrections."
                            title="Steering input"
                            unit="deg"
                        />
                        <TelemetryChart
                            series={[
                                {
                                    label: "Lap 4",
                                    color: COLORS.reference,
                                    points: referenceTrace.points,
                                    key: "rpm",
                                    width: 3,
                                },
                                {
                                    label: `Lap ${comparisonLap}`,
                                    color: COLORS.comparison,
                                    points: comparisonTrace.points,
                                    key: "rpm",
                                    dashed: true,
                                    width: 3,
                                },
                            ]}
                            subtitle="Check shift timing and recovery after minimum speed."
                            title="Engine speed"
                            unit="rpm"
                        />
                        <TelemetryChart
                            max={6}
                            min={0}
                            series={[
                                {
                                    label: "Lap 4",
                                    color: COLORS.reference,
                                    points: referenceTrace.points,
                                    key: "gear",
                                    width: 3,
                                },
                                {
                                    label: `Lap ${comparisonLap}`,
                                    color: COLORS.comparison,
                                    points: comparisonTrace.points,
                                    key: "gear",
                                    dashed: true,
                                    width: 3,
                                },
                            ]}
                            subtitle="See where each shift happened around the lap."
                            title="Gear selection"
                            unit=""
                        />
                        <TelemetryChart
                            series={[
                                {
                                    label: "Lap 4",
                                    color: COLORS.reference,
                                    points: referenceTrace.points,
                                    key: "lateralG",
                                    width: 3,
                                },
                                {
                                    label: `Lap ${comparisonLap}`,
                                    color: COLORS.comparison,
                                    points: comparisonTrace.points,
                                    key: "lateralG",
                                    dashed: true,
                                    width: 3,
                                },
                            ]}
                            subtitle="Compare cornering load through each section."
                            title="Lateral acceleration"
                            unit="g"
                        />
                        <TelemetryChart
                            series={[
                                {
                                    label: "Lap 4",
                                    color: COLORS.reference,
                                    points: referenceTrace.points,
                                    key: "longitudinalG",
                                    width: 3,
                                },
                                {
                                    label: `Lap ${comparisonLap}`,
                                    color: COLORS.comparison,
                                    points: comparisonTrace.points,
                                    key: "longitudinalG",
                                    dashed: true,
                                    width: 3,
                                },
                            ]}
                            subtitle="See where the car is slowing and building speed."
                            title="Longitudinal acceleration"
                            unit="g"
                        />
                    </div>
                </section>

                <p className="rounded-xl border border-gray-200 bg-white p-4 text-sm leading-6 text-gray-600">
                    Session note: lap 4 is the fastest recorded lap in this
                    session and includes an incident. It is a comparison lap,
                    not an ideal lap.
                </p>
            </div>
            <RawTelemetryAnalytics analytics={rawAnalytics} />
        </article>
    )
}

export default IRacingTelemetryReport
